import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { getMe, updateProfile } from "../../../api/profile";
import { getMyProjects } from "../../../api/projects";

import PersonalDetails from "../../elements/profile/PersonalDetails";
import ProjectCard from "../../cards/ProjectCard";

import { ReactComponent as User } from "../../../resources/icons/user.svg";
import { ReactComponent as Edit } from "../../../resources/icons/edit.svg";
import { ReactComponent as Logout } from "../../../resources/icons/logout.svg";
import { ReactComponent as Phone } from "../../../resources/icons/phone.svg";
import { ReactComponent as Email } from "../../../resources/icons/mail.svg";
import { preventCachedImage } from "../../../utils/helperFunctions";
import { toast } from "react-toastify";
import { useAuthContext } from "../../../contexts/authContext";
import LogoutModal from "../../elements/profile/LogoutModal";
import { Project } from "../../../types";
import EditProfileModal from "../../elements/profile/EditProfileModal";

const SupervisorProfile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({
    name: "",
    email: "",
    type: "",
    image: "",
    phone: "",
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const hasFetchedUser = useRef(false);
  const hasFetchedProjects = useRef(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { logout, loggingOut, setUser: setContextUser } = useAuthContext();

  useEffect(() => {
    if (hasFetchedUser.current) return;
    getMe().then(setUser);
    hasFetchedUser.current = true;
  }, []);

  useEffect(() => {
    if (hasFetchedProjects.current) return;
    getMyProjects(1, 30).then((data) => {
      const filtered = data.projects.filter((p: Project) =>
        ["ONGOING", "COMPLETED", "CANCELLED"].includes(p.status)
      );
      setProjects(filtered);
    });
    hasFetchedProjects.current = true;
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-row">
      <div className="flex-1 ml-[17.5rem]">
        <h1 className="p-6 text-2xl font-bold">My profile</h1>

        <div className="px-6 py-12 bg-[#F9FAFB] flex flex-row justify-between">
          <div className="flex flex-row">
            <div className="mr-4 self-center">
              {user.image ? (
                <img
                  className="w-[80px] h-[80px] object-cover rounded-full"
                  src={preventCachedImage(user.image)}
                  alt="Profile"
                />
              ) : (
                <User className="self-center" />
              )}
            </div>
            <div className="flex flex-col self-center">
              <h3 className="mb-1 font-bold text-xl">{user.name}</h3>
              <div>
                <span className="capitalize px-3 py-1 rounded-full text-xs font-medium border shrink-0 text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]">
                  {user.type.charAt(0).toUpperCase() +
                    user.type.slice(1).toLowerCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Edit className="mr-2" />
              Edit profile
            </button>
            <button
              type="button"
              onClick={() => setIsLogoutModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Logout className="w-5 mr-2" />
              Logout
            </button>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-bold mb-4">Personal details</h3>
          <div className="max-w-screen flex flex-wrap gap-x-12 gap-y-4">
            <PersonalDetails
              label="Phone number"
              Icon={Phone}
              info={user.phone}
            />
            <PersonalDetails label="Email" Icon={Email} info={user.email} />
          </div>

          {projects.length > 0 && (
            <>
              <h3 className="text-lg font-bold mb-4 mt-8">Projects</h3>
              <div className="max-w-screen grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    id={project.id}
                    title={project.title}
                    publishedAt={
                      project.publishedAt
                        ? dayjs(project.publishedAt).format("DD.MM.YYYY")
                        : dayjs(project.createdAt).format("DD.MM.YYYY")
                    }
                    status={project.status.toLowerCase()}
                    summary={project.summary || "No summary available."}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        initialValues={{
          name: user.name,
          email: user.email,
          phone: user.phone,
          image: user.image,
        }}
        onSubmit={async (values) => {
          try {
            const updatedUser = await updateProfile(values);
            setUser(updatedUser);
            setContextUser(updatedUser);
            setIsEditModalOpen(false);
            toast.success("Profile updated successfully");
          } catch {
            toast.error("Failed to update profile");
          }
        }}
        userRole="SUPERVISOR"
      />

      <LogoutModal
        isOpen={isLogoutModalOpen}
        closeModal={() => setIsLogoutModalOpen(false)}
        handleLogout={handleLogout}
        loading={loggingOut}
      />
    </div>
  );
};

export default SupervisorProfile;
